import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";

import { Company } from "@interfaces/companies";
import { ConfigList } from "@interfaces/slices";
import { FinancialCompany } from "@interfaces/catalogs";
import { BpmUser, Permission, PermissionV2 } from "@interfaces/users";

export interface SessionSlice {
    auth: {
        isAuthenticate: boolean;
        isVerified: boolean;
        signInStep: string;
    };

    user: BpmUser;
    companies: Array<Company>;
    permissions: Array<Permission>;
    permissionsV2: Array<PermissionV2>;
    financial_inc: Array<FinancialCompany>;
    recovery: {
        username: string;
    };
    source_codes: Array<string>;
    filters: {
        current: string;
        shared_filters: {
            companies: Array<any>;
            financials: Array<Pick<FinancialCompany, "id" | "name" | "code" | "company_id">>;
            fullSearch: {
                text: string;
                searching: boolean;
            };
        };
        configList: Array<ConfigList>;
        downloadingList: boolean;
    };
}

const initialState: SessionSlice = {
    user: {
        id: "0-0-0-0-0",
        name: "",
        last_name: "",
        last_name_2: "",
        rol_id: "0-0-0-0-0",
        created_at: "",
        updated_at: "",
        role: {
            id: "0-0-0-0-0",
            name: "",
            code: "",
        },
        sso_id: "0-0-0-0-0",
        status: "ACTIVE",
        email: "",
        company_id: "0-0-0-0-0",
        last_login_at: "",
        companies: [],
        financials: [],
        preferences: {
            theme: "light",
            avatar: {
                color: "#FFF",
                initials: "",
            },
            sessionOptions: {
                persist: false,
                durationDays: 15,
                defaultVersion: "v1",
            },
            options: {
                downloadListApp: false,
            },
        },
        user_parent_id: "0-0-0-0-0",
    },
    permissions: [],
    permissionsV2: [],
    recovery: {
        username: "",
    },
    companies: [],
    source_codes: [],
    financial_inc: [],

    auth: {
        isAuthenticate: false,
        isVerified: false,
        signInStep: "LOGIN",
    },
    filters: {
        current: "",
        shared_filters: {
            companies: [],
            financials: [],
            fullSearch: {
                text: "",
                searching: false,
            },
        },
        configList: [],
        downloadingList: false,
    },
};

export const sessionSlice = createSlice({
    name: "session",
    initialState,
    reducers: {
        setBpmUserLogged: (state, action: PayloadAction<BpmUser>) => {
            state.user = action.payload;
        },
        setUserPermissions: (state, action: PayloadAction<Array<Permission>>) => {
            state.permissions = action.payload;
        },
        setUserPermissionV2: (state, action: PayloadAction<Array<PermissionV2>>) => {
            state.permissionsV2 = action.payload;
        },
        setCompaniesAssigned: (state, action: PayloadAction<Array<Company>>) => {
            state.companies = action.payload;
        },
        setSourceCodesAssigned: (state, action: PayloadAction<Array<string>>) => {
            state.source_codes = action.payload;
        },
        setFinancialIncAssigned: (state, action: PayloadAction<Array<FinancialCompany>>) => {
            state.financial_inc = action.payload;
        },
        setRecoveryUsername: (state, action: PayloadAction<string>) => {
            state.recovery.username = action.payload;
        },
        loginSuccess: (state, action: PayloadAction<void>) => {
            state.auth.isAuthenticate = true;
        },
        logout: (state, action: PayloadAction<void>) => {
            state.auth.isAuthenticate = false;
            state.auth.isVerified = false;
        },
        setSignInStep: (state, action: PayloadAction<string>) => {
            state.auth.signInStep = action.payload;
        },
        verifiedOTP: (state, action: PayloadAction<void>) => {
            state.auth.isVerified = true;
        },
        setFilterListByCompanies: (state, action: PayloadAction<Array<any>>) => {
            state.filters.shared_filters.companies = action.payload;
        },
        setFilterListByFinancials: (state, action: PayloadAction<any | undefined>) => {
            state.filters.shared_filters.financials = action.payload;
        },
        setCurrentList: (state, action: PayloadAction<string>) => {
            state.filters.current = action.payload;
        },
        setConfigList: (state, action: PayloadAction<ConfigList>) => {
            const exists = state.filters.configList.find((t) => t.code === action.payload.code);
            state.filters.configList =
                exists === undefined
                    ? state.filters.configList.concat([{ ...action.payload, sort: [...action.payload.sort] }])
                    : state.filters.configList.map((t) =>
                          t.code === action.payload.code
                              ? { ...t, filters: action.payload.filters, sort: [...action.payload.sort] }
                              : t,
                      );
        },
        setFiltersByCode: (state, action: PayloadAction<{ code: string; filters: GridFilterModel }>) => {
            const exists = state.filters.configList.find((t) => t.code === action.payload.code);

            state.filters.configList =
                exists === undefined
                    ? state.filters.configList.concat([
                          { ...action.payload, sort: [], pagination: { page: 0, pageSize: 20 }, rowCount: 0 },
                      ])
                    : state.filters.configList.map((t) =>
                          t.code === action.payload.code ? { ...t, filters: action.payload.filters } : t,
                      );
        },
        setSortsByCode: (state, action: PayloadAction<{ code: string; sort: GridSortModel }>) => {
            const exists = state.filters.configList.find((t) => t.code === action.payload.code);

            state.filters.configList =
                exists === undefined
                    ? state.filters.configList.concat([
                          {
                              ...action.payload,
                              sort: [...action.payload.sort],
                              filters: { items: [] },
                              pagination: { page: 0, pageSize: 20 },
                              rowCount: 0,
                          },
                      ])
                    : state.filters.configList.map((t) =>
                          t.code === action.payload.code ? { ...t, sort: [...action.payload.sort] } : t,
                      );
        },
        setPaginationByCode: (state, action: PayloadAction<{ code: string; pagination: GridPaginationModel }>) => {
            const exists = state.filters.configList.find((t) => t.code === action.payload.code);

            state.filters.configList =
                exists === undefined
                    ? state.filters.configList.concat([
                          {
                              ...action.payload,
                              sort: [],
                              filters: { items: [] },
                              rowCount: 0,
                          },
                      ])
                    : state.filters.configList.map((t) =>
                          t.code === action.payload.code ? { ...t, pagination: action.payload.pagination } : t,
                      );
        },
        setRowCountByCode: (state, action: PayloadAction<{ code: string; rowCount: number }>) => {
            const exists = state.filters.configList.find((t) => t.code === action.payload.code);

            state.filters.configList =
                exists === undefined
                    ? state.filters.configList.concat([
                          {
                              ...action.payload,
                              sort: [],
                              filters: { items: [] },
                              pagination: { page: 0, pageSize: 20 },
                          },
                      ])
                    : state.filters.configList.map((t) =>
                          t.code === action.payload.code ? { ...t, rowCount: action.payload.rowCount } : t,
                      );
        },
        setFullSearchText: (state, action: PayloadAction<string>) => {
            state.filters.shared_filters.fullSearch.text = action.payload;
        },
        setFullSearching: (state, action: PayloadAction<boolean>) => {
            state.filters.shared_filters.fullSearch.searching = action.payload;
        },
        resetSession: () => initialState,
        setDownloadingList: (state, action: PayloadAction<boolean>) => {
            state.filters.downloadingList = action.payload;
        },
    },
});

export const {
    //   setApplicationState,
    setBpmUserLogged,
    setUserPermissions,
    setUserPermissionV2,
    setCompaniesAssigned,
    setSourceCodesAssigned,
    setFinancialIncAssigned,
    setRecoveryUsername,
    loginSuccess,
    logout,
    setSignInStep,
    verifiedOTP,
    setFilterListByCompanies,
    setFilterListByFinancials,
    setCurrentList,
    setConfigList,
    setFiltersByCode,
    setSortsByCode,
    setPaginationByCode,
    setRowCountByCode,
    setFullSearchText,
    setFullSearching,
    resetSession,
    setDownloadingList,
} = sessionSlice.actions;
export default sessionSlice.reducer;
